import { useQuery } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { adminDashboardTourId, adminDashboardSteps } from '@/tours/admin';
import {
  Building2,
  ClipboardList,
  Users,
  Package,
  UserPlus,
  ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, ErrorMessage } from '@/components/ui';
import { StatCard, BranchStatusBadge } from '@/components/features/admin';
import { adminApi } from '@/services/api/adminApi';
import { queryKeys } from '@/services/api/queryClient';
import { UserRoleLabels } from '@/types/enums';
import type { UserRole } from '@/types/enums';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/constants/routes';

const AdminDashboardPage: React.FC = () => {
  useTourAutoStart({ tourId: adminDashboardTourId, steps: adminDashboardSteps });
  const navigate = useNavigate();

  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.adminStats,
    queryFn: () => adminApi.getStats(),
    staleTime: 60 * 1000,
  });

  const statCards = [
    {
      icon: Building2,
      label: 'Всего ОЦ',
      value: stats?.total_branches ?? 0,
      color: 'blue' as const,
    },
    {
      icon: ClipboardList,
      label: 'Заявки на модерацию',
      value: stats?.pending_applications_count ?? 0,
      color: 'amber' as const,
    },
    {
      icon: Users,
      label: 'Пользователей',
      value: stats?.total_users ?? 0,
      color: 'green' as const,
    },
    {
      icon: Package,
      label: 'Заказов',
      value: stats?.total_orders ?? 0,
      color: 'purple' as const,
    },
  ];

  if (error) {
    return (
      <div className="animate-fadeIn">
        <ErrorMessage
          message="Не удалось загрузить статистику"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const branchStatuses = stats?.branches_by_status ?? {};
  const userRoles = stats?.users_by_role ?? {};

  const quickActions = [
    { label: 'Операционные центры', icon: Building2, path: ROUTES.ADMIN_BRANCHES },
    { label: 'Заявки', icon: ClipboardList, path: ROUTES.ADMIN_APPLICATIONS },
    { label: 'Пользователи', icon: Users, path: ROUTES.ADMIN_USERS },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Панель администратора</h1>
        <p className="text-sm text-gray-500 mt-1">
          Обзор системы
          {stats && (
            <span className="ml-2 inline-flex items-center gap-1 text-emerald-600">
              <UserPlus className="w-3.5 h-3.5" />
              Новых за 7 дней: {stats.recent_registrations}
            </span>
          )}
        </p>
      </div>

      {/* Stat Cards */}
      <div data-tour="admin:stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <StatCard
            key={card.label}
            icon={card.icon}
            label={card.label}
            value={card.value}
            color={card.color}
            loading={isLoading}
          />
        ))}
      </div>

      {/* Breakdowns + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Branch status breakdown */}
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">ОЦ по статусам</h2>
          </Card.Header>
          <Card.Body>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-6 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : Object.keys(branchStatuses).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Нет данных</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(branchStatuses).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <BranchStatusBadge status={status} size="md" />
                    <span className="text-sm font-semibold text-gray-900 tabular-nums">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Users by role breakdown */}
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Пользователи по ролям</h2>
          </Card.Header>
          <Card.Body>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-6 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : Object.keys(userRoles).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Нет данных</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(userRoles).map(([role, count]) => {
                  const label = UserRoleLabels[role as UserRole] ?? role;
                  return (
                    <div key={role} className="flex items-center justify-between">
                      <span className={cn(
                        'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                        role === 'ADMIN' ? 'bg-violet-100 text-violet-700' :
                        role === 'MANAGER' ? 'bg-blue-100 text-blue-700' :
                        role === 'EMPLOYEE' ? 'bg-teal-100 text-teal-700' :
                        'bg-gray-100 text-gray-700',
                      )}>
                        {label}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 tabular-nums">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Quick Actions */}
        <div data-tour="admin:quick-actions">
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Быстрые действия</h2>
          </Card.Header>
          <Card.Body>
            <div className="space-y-2">
              {quickActions.map((action) => (
                <button
                  key={action.path}
                  onClick={() => navigate(action.path)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all duration-200 text-left group"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                    <action.icon className="w-4 h-4 text-primary-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{action.label}</span>
                  <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-gray-500 transition-colors" />
                </button>
              ))}
            </div>
          </Card.Body>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
